import { Checkbox } from '@lmn/design-system';
import { useTranslation } from '@/app/components/translations/Translation';
import {
  type BaggageFilterChange,
  Filter,
  FilterContainer,
  FilterHeader,
  FilterPrice,
  FilterTitle,
  type HandBaggageFilterChange,
} from '@/app/components/hotel-detail/changeTransport/ChangeTransportFiltersCommon';
import { FilterLoadingLayer } from './ChangeTransportFiltersLoading';
import { useTransportPageFilterTracking } from './hooks/useTransportPageFilterTracking';

type ChangeTransportBaggageFilterProps = {
  baggageIncluded?: BaggageFilter[];
  handBaggageIncluded?: BaggageFilter[];
  onChange: (filterChange: BaggageFilterChange | HandBaggageFilterChange) => void;
};

export default function ChangeTransportBaggageFilter(props: ChangeTransportBaggageFilterProps) {
  const { baggageIncluded, handBaggageIncluded, onChange } = props;
  const { t } = useTranslation();
  const sendStratrackFlightPageFilterEvent = useTransportPageFilterTracking();

  const onChangeWithTracking = ({
    filterChange,
    filterName,
    value,
    deselectFlag,
  }: {
    filterChange: BaggageFilterChange | HandBaggageFilterChange;
    filterName: string;
    value: string;
    deselectFlag: boolean;
  }) => {
    sendStratrackFlightPageFilterEvent({
      filterName,
      value,
      deselectFlag,
    });
    onChange(filterChange);
  };

  return (handBaggageIncluded && handBaggageIncluded.length > 0) || (baggageIncluded && baggageIncluded.length > 0) ? (
    <Filter>
      <FilterHeader>
        <FilterTitle>{t('vacation.common.filters.baggage.title')}</FilterTitle>
      </FilterHeader>
      <FilterContainer
        css={`
          row-gap: 8px;
          position: relative;
        `}
      >
        {handBaggageIncluded?.map((handBaggage) => (
          <LuggageFilterCheck
            key="hand-baggage-included"
            {...{ ...handBaggage, name: t('vacation.common.filters.baggage.hand') }}
            onLuggageFilterChange={() =>
              onChangeWithTracking({
                filterChange: {
                  newState: {
                    handBaggageIncluded: [{ ...handBaggage, selected: !handBaggage.selected }],
                  },
                  event: { type: 'FILTER', origin: 'BAGGAGE_FILTER' },
                },
                filterName: 'HAND_BAGGAGE_FILTER',
                value: `${!handBaggage.selected}`,
                deselectFlag: handBaggage.selected,
              })
            }
          />
        ))}
        {baggageIncluded?.map((baggage) => (
          <LuggageFilterCheck
            key="baggage-included"
            {...{ ...baggage, name: t('vacation.common.filters.baggage.checked') }}
            onLuggageFilterChange={() =>
              onChangeWithTracking({
                filterChange: {
                  newState: { baggageIncluded: [{ ...baggage, selected: !baggage.selected }] },
                  event: { type: 'FILTER', origin: 'BAGGAGE_FILTER' },
                },
                filterName: 'CHECKED_BAGGAGE_FILTER',
                value: `${!baggage.selected}`,
                deselectFlag: baggage.selected,
              })
            }
          />
        ))}
        <FilterLoadingLayer origin="BAGGAGE_FILTER" />
      </FilterContainer>
    </Filter>
  ) : null;
}

type CompanyFilterCheckProps = {
  onLuggageFilterChange: (name: string) => void;
} & CompanyFilter;

function LuggageFilterCheck(props: CompanyFilterCheckProps) {
  const { enabled, selected, name, minPrice, onLuggageFilterChange } = props;
  return (
    <div
      css={`
        display: flex;
        align-items: center;
        justify-content: space-between;
      `}
    >
      <div
        css={`
          display: flex;
          flex-direction: column;
        `}
      >
        <span>{name}</span>
        <FilterPrice price={enabled ? minPrice : 0} />
      </div>
      <Checkbox
        checkbox={{ id: name }}
        disabled={!enabled}
        isChecked={selected}
        onChange={() => onLuggageFilterChange(name)}
      />
    </div>
  );
}
